import React, { useContext, useEffect, FocusEvent } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { ClientsContext } from "../context/ClientsContextProvider";
import { Client, PostClient } from "../types";
import TextInput from "../../../main/components/TextInput";
import {
  clientDetailsDataToClient,
  clientToClientDetailsData,
} from "../clientDetailsMapper";
import { Grid, Typography } from "@mui/material";
import CnpjInput from "../../../main/components/CnpjInput";
import { cnpjRegex } from "../../../main/utils/utils";

const schema = yup.object().shape({
  name: yup.string().nullable(),
  zipCode: yup.string().nullable(),
  cnpj: yup
    .string()
    .nullable()
    .transform((originalValue) => {
      const cleanedValue = originalValue
        ? originalValue.replace(/[^\d]+/g, "")
        : null;
      return cleanedValue;
    })
    .matches(cnpjRegex, "Formato de CNPJ inválido"),
  street: yup.string().nullable(),
  streetNumber: yup.string().nullable(),
  complement: yup.string().nullable(),
  city: yup.string().nullable(),
  state: yup.string().nullable(),
});

export type ClientDetailsData = yup.InferType<typeof schema>;

const ClientDetailsForm = React.forwardRef(
  (
    props: {
      selectedStep: number;
      setSelectedStep: React.Dispatch<React.SetStateAction<number>>;
    },
    ref
  ) => {
    const { client: client, setClient: setClient } = useContext(ClientsContext);
    // const { isMobile } = useContext(MainContext);
    const {
      handleSubmit,
      control,
      formState: { errors },
      setValue,
      setError,
      clearErrors,
      trigger,
    } = useForm({
      resolver: yupResolver(schema),
    });

    const checkCEP = async (e: FocusEvent<HTMLInputElement>) => {
      if (!e.target.value) return;
      clearErrors("zipCode");
      const cep = e.target.value.replace(/\D/g, "");
      const validacep = /^[0-9]{8}$/;
      if (!validacep.test(cep)) {
        setError("zipCode", {
          type: "manual",
          message: "CEP inválido",
        });
        return;
      }
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();
      if (data.erro) {
        setError("zipCode", {
          type: "manual",
          message: "CEP não encontrado",
        });
        return;
      }
      setValue("street", data.logradouro);
      setValue("city", data.localidade);
      setValue("state", data.uf);
    };
    function updateData() {
      if (!client) {
        setValue("name", null);
        setValue("zipCode", null);
        setValue("cnpj", null);
        setValue("street", null);
        setValue("streetNumber", null);
        setValue("complement", null);
        setValue("city", null);
        setValue("state", null);
      } else {
        const data = clientToClientDetailsData(client);
        if (!data) return;
        setValue("name", data.name);
        setValue("zipCode", data.zipCode);
        setValue("cnpj", data.cnpj);
        setValue("street", data.street);
        setValue("streetNumber", data.streetNumber);
        setValue("complement", data.complement);
        setValue("city", data.city);
        setValue("state", data.state);
      }
    }
    useEffect(() => {
      updateData();
    }, [client]);
    const onSubmit = async (data: ClientDetailsData) => {
      const clientDetails = clientDetailsDataToClient(data);
      const newClientData: PostClient | Client = {
        ...client,
        ...clientDetails,
      };
      setClient(newClientData);
      props.setSelectedStep(props.selectedStep + 1);
    };

    React.useImperativeHandle(ref, () => ({
      async submitForm() {
        await handleSubmit(onSubmit)();
      },
    }));
    const handleCnpjFieldBlur = async () => {
      try {
        await trigger("cnpj"); // Aciona a validação do campo CNPJ
      } catch (error) {}
    };
    return (
      <>
        <Grid
          spacing={2}
          container
          component={"form"}
          width={"100%"}
          onSubmit={handleSubmit(onSubmit)}
          paddingX={"10px"}
        >
          <Grid item xs={12} textAlign={"start"}>
            <Typography
              variant="h6"
              sx={{
                borderBottom: "1px solid #ccc",
                paddingBottom: 1,
                marginBottom: "15px",
              }}
            >
              Dados
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextInput
              fieldName="name"
              label="Nome"
              control={control}
              errors={errors}
              sx={{
                width: "100%",
                // maxWidth: "280px",
              }}
            />
          </Grid>

          <Grid display={"flex"} item xs={12} md={6}>
            <CnpjInput
              fieldName="cnpj"
              label="CNPJ"
              control={control}
              errors={errors}
              sx={{
                width: "100%",
                // maxWidth: "280px",
              }}
              onBlur={() => handleCnpjFieldBlur()}
            />
          </Grid>
          <Grid display={"flex"} item xs={12} md={3}>
            <TextInput
              fieldName="zipCode"
              label="CEP"
              control={control}
              errors={errors}
              sx={{
                width: "100%",
                // maxWidth: "280px",
              }}
              onBlur={checkCEP}
            />
          </Grid>
          <Grid display={"flex"} item xs={12} md={3}>
            <TextInput
              fieldName="street"
              label="Rua"
              control={control}
              errors={errors}
              sx={{
                width: "100%",
                // maxWidth: "280px",
              }}
            />
          </Grid>
          <Grid display={"flex"} item xs={12} md={3}>
            <TextInput
              fieldName="streetNumber"
              label="Número"
              control={control}
              errors={errors}
              sx={{
                width: "100%",
                // maxWidth: "280px",
              }}
            />
          </Grid>
          <Grid display={"flex"} item xs={12} md={3}>
            <TextInput
              fieldName="complement"
              label="Complemento"
              control={control}
              errors={errors}
              sx={{
                width: "100%",
                // maxWidth: "280px",
              }}
            />
          </Grid>
          <Grid display={"flex"} item xs={12} md={6}>
            <TextInput
              fieldName="city"
              label="Cidade"
              control={control}
              errors={errors}
              sx={{
                width: "100%",
                // maxWidth: "280px",
              }}
            />
          </Grid>
          <Grid display={"flex"} item xs={12} md={6}>
            <TextInput
              fieldName="state"
              label="Estado"
              control={control}
              errors={errors}
              sx={{
                width: "100%",
                // maxWidth: "280px",
              }}
            />
          </Grid>
        </Grid>
      </>
    );
  }
);

export default ClientDetailsForm;
